/**
 * Eenheden in het meervoud zetten: "2 potten" in plaats van "2 pot".
 *
 * De eenheid is een vrij tekstveld — iemand kan alles intypen — dus dit is
 * regelgebaseerd en niet een lijstje met alle mogelijke woorden. De regels
 * dekken wat er in de praktijk wordt ingevuld (pak, pot, bakje, krat, doos);
 * bij iets exotisch valt het terug op de gewone -en-uitgang.
 */
const KLINKERS = 'aeiou';
/** Maten blijven in het Nederlands enkelvoud na een telwoord: "2 liter melk".
 *  De afkortingen staan er los bij: wie `gr` of `ltr` typt bedoelt hetzelfde
 *  als wie `gram` of `liter` typt, en zonder die regels wordt het "2 gren". */
const MATEN = new Set([
    'liter', 'l', 'ltr', 'ml', 'cl', 'dl',
    'kilo', 'kilogram', 'kg', 'gram', 'gr', 'g', 'mg', 'ons', 'pond',
    'meter', 'm', 'cm', 'mm', 'procent', 'euro',
    // Afkortingen voor stuks en lepels. `el` en `tl` moeten hier staan omdat de
    // -s-regel hieronder ze anders tot "els" en "tls" maakt.
    'st', 'stk', 'el', 'tl',
]);
/** Enkelvoudige eenheden die op een -s eindigen. Zonder deze lijst ziet
 *  `isAlMeervoud` ze voor een meervoud aan en blijft "2 fles" staan. */
const ENKELVOUD_OP_S = new Set(['fles', 'bus', 'doos', 'bos', 'tros', 'glas', 'kaas']);
/** Wat niet uit de regels volgt. */
const UITZONDERINGEN = {
    stuk: 'stuks',
    ei: 'eieren',
    blad: 'bladen',
    // Leenwoord: krijgt de Engelse -s en niet "setten".
    set: 'sets',
    snee: 'sneeën',
    // Korte klank die in het meervoud toch lang wordt; volgt uit geen enkele regel.
    glas: 'glazen',
};
const isKlinker = (teken) => KLINKERS.includes(teken);
/**
 * Staat dit woord er al in het meervoud?
 *
 * Het eenheidsveld is vrije tekst, en de app toont zelf "2 pakken" — dus dat
 * typen mensen ook zo weer in. Zonder deze controle wordt dat "2 pakkens".
 * Voor eenheden is de vuistregel scherp genoeg: een -en-uitgang is altijd
 * meervoud (er is geen enkelvoudige eenheid die zo eindigt), en een -s-uitgang
 * ook, op de handvol woorden in `ENKELVOUD_OP_S` na.
 */
function isAlMeervoud(woord) {
    if (woord.length < 3)
        return false;
    if (woord.endsWith("'s"))
        return true;
    if (woord.endsWith('en'))
        return true;
    if (woord.endsWith('s'))
        return !ENKELVOUD_OP_S.has(woord);
    return false;
}
/** Enkele klinker tussen twee medeklinkers: pot → potten, zak → zakken. */
function heeftKorteKlank(woord) {
    if (woord.length < 3)
        return false;
    const [voor, klinker, eind] = [woord.at(-3), woord.at(-2), woord.at(-1)];
    return !isKlinker(voor) && isKlinker(klinker) && !isKlinker(eind);
}
/** Dubbelgeschreven lange klank: doos → dozen, boot → boten. */
function heeftLangeKlank(woord) {
    if (woord.length < 4)
        return false;
    const [een, twee, eind] = [woord.at(-3), woord.at(-2), woord.at(-1)];
    return een === twee && isKlinker(een) && !isKlinker(eind);
}
/** Lange klank geschreven als `ie`: brief → brieven, kies → kiezen. Anders dan
 *  bij doos → dozen blijft de klinker hier voluit staan. */
function heeftIeKlank(woord) {
    if (woord.length < 4)
        return false;
    return woord.at(-3) === 'i' && woord.at(-2) === 'e' && !isKlinker(woord.at(-1));
}
/** De medeklinker die tussen twee klinkers komt te staan: s wordt z, f wordt v. */
function verzacht(eind, oorspronkelijk) {
    if (eind === 's')
        return 'z';
    if (eind === 'f')
        return 'v';
    return oorspronkelijk;
}
/**
 * De eenheid passend bij `aantal`. Bij één of minder blijft hij zoals hij is,
 * daarboven gaat hij in het meervoud.
 */
export function meervoudEenheid(eenheid, aantal) {
    const woord = eenheid.trim();
    if (!woord || aantal <= 1)
        return woord;
    const laag = woord.toLowerCase();
    if (MATEN.has(laag))
        return woord;
    if (isAlMeervoud(laag))
        return woord;
    const uitzondering = UITZONDERINGEN[laag];
    if (uitzondering) {
        return woord[0] === laag[0] ? uitzondering : uitzondering[0].toUpperCase() + uitzondering.slice(1);
    }
    // Verkleinwoorden en onbeklemtoonde uitgangen krijgen -s: bakje → bakjes,
    // emmer → emmers, tube → tubes.
    if (/(je|el|em|er|aar|e)$/.test(laag))
        return `${woord}s`;
    // Klinker op het eind krijgt een apostrof: silo → silo's.
    if (isKlinker(laag.at(-1)))
        return `${woord}'s`;
    // Samenstellingen met een cijfer of streepje volgen het Engelse patroon:
    // 6-pack → 6-packs.
    if (/[^a-zà-ÿ]/.test(laag))
        return `${woord}s`;
    if (heeftKorteKlank(laag))
        return `${woord}${woord.at(-1)}en`;
    if (heeftLangeKlank(laag)) {
        // Eén klinker valt weg: doos → dozen, boot → boten.
        return `${woord.slice(0, -2)}${verzacht(laag.at(-1), woord.at(-1))}en`;
    }
    if (heeftIeKlank(laag)) {
        // De `ie` blijft staan: brief → brieven.
        return `${woord.slice(0, -1)}${verzacht(laag.at(-1), woord.at(-1))}en`;
    }
    return `${woord}en`;
}
/** "3 potten", "1 pak", of "3×" als er geen eenheid is ingevuld. */
export function toonAantal(aantal, eenheid) {
    const woord = meervoudEenheid(eenheid, aantal);
    return woord ? `${aantal} ${woord}` : `${aantal}×`;
}
//# sourceMappingURL=eenheid.js.map